import {t} from "../../i18n";

import "./RoundResults.scss";
import {useContext, useEffect} from "react";
import {DisplayConfig} from "../Config";
import {
    AllianceColor,
    DisplayCommand,
    HighScoreLevel,
    MatchAlliance,
    MatchResultsTeam,
    RankMove,
    ShowResults, ShowRoundResults,
    TournamentLevel
} from "../../javaTypes";
import DisplayParams from "../DisplayParams";
import {RankingData} from "../rankingDataState";
import {isDoubleElimsSeriesDescription} from "../util";
import PrimaryMatch from "../layouts/PrimaryMatch";
import {ScoreType} from "../components/decode/FullScore";
import FormattedSeed from "../components/FormattedSeed";
import EventLogo from "../components/EventLogo";
import ScrollingText from "../components/ScrollingText";
import play from "../../sounds";

function RefCard({card} : {
    card : number
}) {
    if(card === 0) return null;

    return <div className={`resultsCards ${card & 1 && card & 2 ? "twoCards" : ""}`}>
        &nbsp;
        {card & 1 ? <div className={"yellowCard"} /> : null }
        {card & 2 ? <div className={"redCard"} /> : null }
    </div>
}

RefCard.propTypes = {};

function ResultsSet({color, alliance, scoreDetail, otherScoreDetail, tournamentLevel, record, seed, score, winner, highScore, highScoreFlavor, rightAligned, hideRankMove, showLeagueRankings} : {
    color: 'red' | 'blue' | 'orange',
    alliance: MatchAlliance<MatchResultsTeam>,
    scoreDetail: Partial<ScoreType>,
    otherScoreDetail: Partial<ScoreType>,
    tournamentLevel: TournamentLevel,
    record?: number,
    seed: string,
    score: number,
    winner?: AllianceColor,
    highScore: boolean,
    highScoreFlavor: string,
    rightAligned: boolean,
    hideRankMove?: boolean,
    showLeagueRankings?: boolean,
}) {
    let teams = !alliance.teams ? [] : alliance.teams;
    const hasRank = tournamentLevel == TournamentLevel.QUALIFICATION;
    const isPlayoffs = tournamentLevel == TournamentLevel.PLAYOFF;
    const items = teams.map((team, idx) => {
        if (team.number === "") {
            return null;
        }
        return <div key={idx} className={"roundResultsTeam"}>
            <div className={`team-avatar team-${team.number}`}></div> {team.number}
        </div>;
    })

    const showWinner = color !== 'orange' && (!winner || winner.toLowerCase() === color);
    const earnedCP = (teams[0].card & 2) ? 0 : (!winner ? 1 : (winner?.toLowerCase() == color ? 2 : 0));

    return <div className={`roundResultsSet ${color} ${rightAligned ? 'right' : ''} ${showLeagueRankings ? 'hasLeagueRankings' : 'null'} ${showWinner ? '' : 'noWinner'}`}>
        {showWinner ? <div className={`winnerBanner ${winner ? 'won' : ''}`}><span className={"winSpan"}>{!winner ? t`Tie` : t`Winner`}</span>{winner ? <i className={"fa fa-trophy"} /> : null}</div> : null}
        <div className={"allianceScore"}>
            <span className={"scoreSpan"}>{score}</span>
            <span className={"cpSpan"}>(+{earnedCP} CP)</span>
        </div>
        <div className={"roundRankings"}>
            {highScore ? <span className={"highScoreBanner"}><i className={"fa fa-star"}></i> {highScoreFlavor} <i className={"fa fa-star"}></i></span> : null}

            <table>
                <tbody>
                <tr>
                    <th>{t`Rank`}</th>
                    <td colSpan={hideRankMove ? 2 : 1}>{teams[0].ranking}</td>
                    {hideRankMove ? null : <td>{(teams[0].rankMove === RankMove.DOWN ? <i className={"fa fa-arrow-down"}></i> : (teams[0].rankMove === RankMove.UP ?
                        <i className={"fa fa-arrow-up"}></i> : '-'))}</td>}
                </tr>
                <tr>
                    <th>{t`Record`}</th>
                    <td colSpan={2}>{alliance.wins}-{alliance.losses}-{alliance.ties}</td>
                </tr>
                </tbody>
            </table>
            {isPlayoffs && teams[0].card ? <div className={"roundElimsCard"}>{t`Issued Card:`} <RefCard card={teams[0].card} /></div> : null}
        </div>
        {isPlayoffs && seed ? <div className={"allianceName"}><ScrollingText><FormattedSeed seed={seed} /></ScrollingText></div> :
            <span className={"allianceName"}>{color == 'red' ? 'Red' : 'Blue'}</span>}
        <div className={"allianceTeams"}>
            {items}
        </div>
    </div>;
}

const HIGH_SCORE_TEXT: Record<HighScoreLevel, string> = {
    [HighScoreLevel.DIVISION]: t`High Score`,
    [HighScoreLevel.EVENT]: t`High Score`,
    [HighScoreLevel.REGION]: t`High Score`,
    [HighScoreLevel.WORLD]: t`High Score`,
}

function SingleMatchResults({results, allianceOrientation}: {
    results: ShowResults,
    allianceOrientation: "standard" | "flipped"
}) {
    const params = useContext(DisplayParams);
    const state = useContext(RankingData);

    const structure = state[params.divisionNumber || 0]?.playoffStructure;
    const seriesDescription = structure?.series.find(s => s.series == results.series);

    const sets = (allianceOrientation === 'flipped' ? ['blue', 'red'] as const : ['red', 'blue'] as const).map((a, idx) => <ResultsSet
        key={a}
        color={a}
        alliance={results[a]}
        scoreDetail={results[`${a}Scores`]}
        otherScoreDetail={results[`${a === 'red' ? 'blue' : 'red'}Scores`]}
        tournamentLevel={results.tournamentLevel}
        record={seriesDescription && (isDoubleElimsSeriesDescription(seriesDescription) || seriesDescription.matchesToWin <= 1) ? undefined : results[a].wins}
        seed={results[a].seed}
        score={results[`${a}Score`]}
        winner={results.winner}
        highScore={results[`${a}HighScore`]}
        highScoreFlavor={HIGH_SCORE_TEXT[results.highScoreLevel]}
        rightAligned={idx === 1}
        hideRankMove={seriesDescription?.round == 1}
    />);


    return <div className={`roundResultsWrapper`}>
        {sets[0]}
        <div className={"roundMatchName"}>
            {results.matchName}
        </div>
        {sets[1]}
    </div>
}

export default function RoundResults({config, command, nowCallback} : {
    config: DisplayConfig,
    command: DisplayCommand<ShowRoundResults>,
    nowCallback: () => number,
}) {
    const params = useContext(DisplayParams);
    useEffect(() => {
        if(nowCallback() - command.ts < 1000 && !config.mute && !config.muteRandomizationResults) {
            play('results');
        }
    }, [command.ts]);

    return <PrimaryMatch pageTitle={<><EventLogo logo={params.matchPrefixLogo} /> Round {command.params.round}</>} pageName={t`Match Results`} className={"roundResultsPage"} smallTop config={config}>
        <div className={"roundResultsContainer"}>
            {command.params.results.map((result, idx) => <SingleMatchResults key={idx} results={result} allianceOrientation={config.allianceOrientation} />)}
        </div>
    </PrimaryMatch>;
}
